// src/components/TicketButtons/AvailabilityChangeButton.tsx
import React from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
  Typography,
  Divider,
  Chip,
  OutlinedInput,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { createAvailabilityChange, AvailabilityChangePayload } from '../../api/ticketingApi';
import { useAuth } from '../../context/AuthContext';
import { useProfesorPerfil } from '../../hooks/useProfesorFicha';
import { useTranslation } from 'react-i18next';

/**
 * Wrapper: solo muestra el botón y monta el diálogo de forma perezosa.
 */
type Props = {
  buttonText?: string;
  onSuccess?: (ticketId: string) => void;
  variant?: 'text' | 'outlined' | 'contained';
  color?: 'primary' | 'secondary' | 'inherit' | 'success' | 'error' | 'info' | 'warning';
  size?: 'small' | 'medium' | 'large';
};

export default function AvailabilityChangeButton({
  buttonText,
  onSuccess,
  variant = 'outlined',
  color = 'primary',
  size = 'small',
}: Props) {
  const { t } = useTranslation<'translation'>();
  const defaultButtonText = t('availabilityChange.button');
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <Button
        startIcon={<EventAvailableIcon />}
        variant={variant}
        color={color}
        size={size}
        onClick={() => setOpen(true)}
        title="Solicitar cambio de disponibilidad"
        sx={{
          color: '#21a598',
          borderColor: '#21a598',
          '&:hover': {
            backgroundColor: 'rgba(33,165,152,0.08)',
            borderColor: '#1b887f',
            color: '#1b887f',
          },
        }}
      >
        {buttonText || defaultButtonText}
      </Button>

      {open && (
        <AvailabilityChangeDialog open onClose={() => setOpen(false)} onSuccess={onSuccess} />
      )}
    </>
  );
}

/* ========================================================================== */
/*                               Dialog internals                              */
/* ========================================================================== */

type Range = { start: string; end: string; centroId?: number | null; formatoId?: number };
type WeeklyState = Record<number, Range[]>; // 1..7

// Payload extendido (backend ya soporta estos campos adicionales)
type ExtendedPayload = AvailabilityChangePayload & {
  centrosIds?: number[];
  formatosIds?: number[];
  horasSemanales?: number;
  weekly?: {
    weekday: number;
    ranges: {
      start: string;
      end: string;
      centroId?: number | null;
      formatoId?: number;
    }[];
  }[];
};

/* ------------------------------ helpers fecha ----------------------------- */

const MIN_LEAD_DAYS = 15;

function ymdLocal(d: Date): string {
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function addDaysLocal(base: Date, days: number): Date {
  return new Date(base.getFullYear(), base.getMonth(), base.getDate() + days);
}
function parseYmdLocal(s: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}
function daysFromToday(dateStr: string): number | null {
  const d = parseYmdLocal(dateStr);
  if (!d) return null;
  const today = new Date();
  const t0 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const diffMs = d.getTime() - t0.getTime();
  return Math.round(diffMs / (1000 * 60 * 60 * 24));
}

// dayLabels se obtendrá dinámicamente usando traducciones

const hhmmOk = (v: string) => /^([01]\d|2[0-3]):[0-5]\d$/.test(v);

function AvailabilityChangeDialog({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: (ticketId: string) => void;
}) {
  const { t } = useTranslation<'translation'>();
  const { user } = useAuth();
  const { data: perfil, loading: perfilLoading, error: perfilError } = useProfesorPerfil(user?.id);

  const dayLabels: Record<number, string> = {
    1: t('availabilityChange.dias.lunes'),
    2: t('availabilityChange.dias.martes'),
    3: t('availabilityChange.dias.miercoles'),
    4: t('availabilityChange.dias.jueves'),
    5: t('availabilityChange.dias.viernes'),
    6: t('availabilityChange.dias.sabado'),
    7: t('availabilityChange.dias.domingo'),
  };

  // Catálogos TOMADOS DEL PERFIL (sin llamadas extra)
  const centros = React.useMemo(
    () =>
      (perfil?.centros ?? [])
        .map((c: any) => {
          const id = c?.centro?.id ?? c?.idCentro ?? c?.centroId ?? c?.id;
          const nombre = c?.centro?.nombre ?? c?.nombre ?? `Centro ${id ?? '?'}`;
          return Number.isFinite(Number(id)) ? { id: Number(id), nombre: String(nombre) } : null;
        })
        .filter(Boolean) as { id: number; nombre: string }[],
    [perfil],
  );

  const formatos = React.useMemo(
    () =>
      (perfil?.formatos ?? [])
        .map((f: any) => {
          const id = f?.formato?.id ?? f?.id;
          const nombre =
            f?.formato?.nombreFormato ??
            f?.nombreFormato ??
            f?.formato?.nombre ??
            f?.nombre ??
            `Formato ${id ?? '?'}`;
          return Number.isFinite(Number(id))
            ? { id: Number(id), nombreFormato: String(nombre) }
            : null;
        })
        .filter(Boolean) as { id: number; nombreFormato: string }[],
    [perfil],
  );

  const centroDict = React.useMemo(
    () => Object.fromEntries(centros.map((c) => [c.id, c.nombre])),
    [centros],
  );
  const formatoDict = React.useMemo(
    () => Object.fromEntries(formatos.map((f) => [f.id, f.nombreFormato])),
    [formatos],
  );

  // Estado general
  const [loading, setLoading] = React.useState(false);
  const [snack, setSnack] = React.useState<{
    open: boolean;
    msg: string;
    sev: 'success' | 'error';
  }>({
    open: false,
    msg: '',
    sev: 'success',
  });

  // Fechas (derivamos el tipo a partir de ellas)
  const today = React.useMemo(() => new Date(), []);
  const defaultFrom = React.useMemo(() => ymdLocal(addDaysLocal(today, MIN_LEAD_DAYS)), [today]);

  const [effectiveFrom, setEffectiveFrom] = React.useState<string>(defaultFrom);
  const [effectiveTo, setEffectiveTo] = React.useState<string>('');

  const fromDays = daysFromToday(effectiveFrom);
  const fromTooSoon = typeof fromDays === 'number' && fromDays < MIN_LEAD_DAYS;
  const toBeforeFrom =
    effectiveTo && effectiveFrom
      ? parseYmdLocal(effectiveTo)!.getTime() < parseYmdLocal(effectiveFrom)!.getTime()
      : false;

  // Resto de campos
  const [timezone, setTimezone] = React.useState<string>(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch {
      return '';
    }
  });
  const [reasonCode, setReasonCode] = React.useState<string>('');
  const [comment, setComment] = React.useState<string>('');

  // Ámbito global
  const [centrosIds, setCentrosIds] = React.useState<number[]>([]);
  const [formatosIds, setFormatosIds] = React.useState<number[]>([]);
  const [horasSemanales, setHorasSemanales] = React.useState<string>('');

  // Semana tipo
  const [weekly, setWeekly] = React.useState<WeeklyState>({});
  const [tmpStart, setTmpStart] = React.useState<Record<number, string>>({});
  const [tmpEnd, setTmpEnd] = React.useState<Record<number, string>>({});
  const [tmpCentro, setTmpCentro] = React.useState<Record<number, string>>({});
  const [tmpFormato, setTmpFormato] = React.useState<Record<number, string>>({});
  const [files, setFiles] = React.useState<File[]>([]);

  const onChooseFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setFiles(Array.from(e.target.files));
  };

  const addRange = (weekday: number) => {
    const rawS = (tmpStart[weekday] ?? '').trim();
    const rawE = (tmpEnd[weekday] ?? '').trim();
    // Normalizar entradas tipo "9:00" -> "09:00"
    const normalize = (v: string) => {
      const m = v.match(/^(\d{1,2}):?(\d{2})$/);
      if (!m) return v;
      const h = m[1].padStart(2, '0');
      const mm = m[2];
      return `${h}:${mm}`;
    };
    const s = normalize(rawS);
    const e = normalize(rawE);
    // Debug: mostrar tmp antes de validación
    if (typeof window !== 'undefined' && (console && console.debug)) {
      console.debug('[AvailabilityChange] addRange - tmp values:', { weekday, rawS, rawE, s, e });
    }
    if (!hhmmOk(s) || !hhmmOk(e) || e <= s) {
      setSnack({ open: true, msg: t('availabilityChange.error.rangoInvalido'), sev: 'error' });
      return;
    }

    // Centro / Formato opcionales
    const centroSel = (tmpCentro[weekday] ?? '').trim();
    const formatoSel = (tmpFormato[weekday] ?? '').trim();

    let centroId: number | null | undefined = undefined;
    if (centroSel === 'null') centroId = null;
    else if (centroSel) {
      const n = Number(centroSel);
      if (Number.isFinite(n)) centroId = n;
    }

    let formatoId: number | undefined = undefined;
    if (formatoSel) {
      const n = Number(formatoSel);
      if (Number.isFinite(n)) formatoId = n;
    }

    setWeekly((prev) => {
      const list = prev[weekday] ? [...prev[weekday]] : [];
      const item: Range = { start: s, end: e };
      if (centroId !== undefined) item.centroId = centroId;
      if (formatoId !== undefined) item.formatoId = formatoId;
      list.push(item);
      return { ...prev, [weekday]: list };
    });
    // Debug: mostrar estado weekly tras añadir rango
    if (typeof window !== 'undefined' && (console && console.debug)) {
      console.debug('[AvailabilityChange] addRange - weekly after add:', {
        weekday,
        start: s,
        end: e,
        centroId,
        formatoId,
      });
    }
    setTmpStart((p) => ({ ...p, [weekday]: '' }));
    setTmpEnd((p) => ({ ...p, [weekday]: '' }));
    setTmpCentro((p) => ({ ...p, [weekday]: '' }));
    setTmpFormato((p) => ({ ...p, [weekday]: '' }));
  };

  const removeRange = (weekday: number, idx: number) => {
    setWeekly((prev) => {
      const list = [...(prev[weekday] ?? [])];
      list.splice(idx, 1);
      const next = { ...prev };
      if (list.length) next[weekday] = list;
      else delete next[weekday];
      return next;
    });
  };

  const buildWeeklyPayload = (): ExtendedPayload['weekly'] | undefined => {
    // Construir payload a partir del estado 'weekly' y añadir como fallback
    // cualquier rango que el usuario haya dejado en los inputs temporales (tmpStart/tmpEnd)
    // pero no haya pulsado "+" para añadir.
    const merged: Record<number, Range[]> = {};

    // Copiar lo que ya existe en state
    for (const k of Object.keys(weekly)) {
      const num = Number(k);
      if (!Number.isFinite(num)) continue;
      merged[num] = (weekly as any)[k].map((r: any) => ({ ...r }));
    }

    // Añadir rangos desde tmpStart/tmpEnd si están presentes y válidos
    for (let wd = 1; wd <= 7; wd++) {
      const rawS = (tmpStart[wd] ?? '').trim();
      const rawE = (tmpEnd[wd] ?? '').trim();
      if (!rawS || !rawE) continue;
      const normalize = (v: string) => {
        const m = v.match(/^(\d{1,2}):?(\d{2})$/);
        if (!m) return v;
        const h = m[1].padStart(2, '0');
        const mm = m[2];
        return `${h}:${mm}`;
      };
      const s = normalize(rawS);
      const e = normalize(rawE);
      if (!hhmmOk(s) || !hhmmOk(e) || e <= s) continue;
      const centroSel = (tmpCentro[wd] ?? '').trim();
      const formatoSel = (tmpFormato[wd] ?? '').trim();
      let centroId: number | null | undefined = undefined;
      if (centroSel === 'null') centroId = null;
      else if (centroSel) {
        const n = Number(centroSel);
        if (Number.isFinite(n)) centroId = n;
      }
      let formatoId: number | undefined = undefined;
      if (formatoSel) {
        const n = Number(formatoSel);
        if (Number.isFinite(n)) formatoId = n;
      }
      if (!merged[wd]) merged[wd] = [];
      merged[wd].push({ start: s, end: e, centroId, formatoId });
    }

    const keys = Object.keys(merged).map(Number).sort((a, b) => a - b);
    if (!keys.length) return undefined;
    if (typeof window !== 'undefined' && (console && console.debug)) {
      console.debug('[AvailabilityChange] buildWeeklyPayload - merged weekly state:', merged);
    }
    return keys.map((k) => ({
      weekday: k,
      ranges: merged[k].map((r) => {
        const out: { start: string; end: string; centroId?: number | null; formatoId?: number } = {
          start: r.start,
          end: r.end,
        };
        if (r.centroId !== undefined) out.centroId = r.centroId;
        if (r.formatoId !== undefined) out.formatoId = r.formatoId;
        return out;
      }),
    }));
  };

  const submit = async () => {
    if (!effectiveFrom) {
      setSnack({
        open: true,
        msg: t('availabilityChange.error.fechaInicioRequerida'),
        sev: 'error',
      });
      return;
    }
    if (toBeforeFrom) {
      setSnack({ open: true, msg: t('availabilityChange.error.fechaHastaAnterior'), sev: 'error' });
      return;
    }

    setLoading(true);
    try {
      const weeklyPayload = buildWeeklyPayload();

      // Debug: mostrar weekly y payload justo antes de enviar
      if (typeof window !== 'undefined' && (console && console.debug)) {
        console.debug('[AvailabilityChange] submit - weekly state:', weekly);
        console.debug('[AvailabilityChange] submit - weeklyPayload:', weeklyPayload);
      }

      // Derivar tipo según haya fin o no
      const derivedType: ExtendedPayload['type'] = effectiveTo ? 'temporal' : 'permanente';

      const payload: ExtendedPayload = {
        type: derivedType,
        effectiveFrom,
        effectiveTo: effectiveTo || undefined, // opcional
        timezone: timezone || undefined,
        reasonCode: reasonCode || undefined,
        // Asegurarnos de enviar siempre un array (vacío si no hay tramos)
        weekly: weeklyPayload ?? [],
        comment: comment.trim() || undefined,
        files,
        // ámbito global (opcionales)
        centrosIds: centrosIds.length ? centrosIds : undefined,
        formatosIds: formatosIds.length ? formatosIds : undefined,
        horasSemanales: horasSemanales ? Number(horasSemanales) : undefined,
      };

      const res = await createAvailabilityChange(payload as any);
      const ticketId = (res as any).id || (res as any).ticketId || '';
      setSnack({ open: true, msg: t('availabilityChange.success', { ticketId }), sev: 'success' });
      onSuccess?.(ticketId);
      onClose();
    } catch (e: any) {
      const msg = e?.response?.data?.message || t('availabilityChange.error.crearError');
      setSnack({ open: true, msg, sev: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const renderSelectedChips = (selected: (string | number)[], dict: Record<number, string>) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {selected.map((id) => (
        <Chip key={id} label={dict[Number(id)] ?? id} size="small" />
      ))}
    </Box>
  );

  return (
    <>
      <Dialog open={open} onClose={() => !loading && onClose()} maxWidth="md" fullWidth>
        <DialogTitle>{t('availabilityChange.title')}</DialogTitle>

        <DialogContent>
          {/* Si el perfil aún está cargando, muestra feedback (normalmente ya estará cargado) */}
          {perfilLoading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 3 }}>
              <CircularProgress />
            </Stack>
          ) : perfilError ? (
            <Alert severity="error" sx={{ my: 2 }}>
              No se pudieron cargar los catálogos del perfil.
            </Alert>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {/* Fechas (hasta opcional) */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Desde"
                  value={effectiveFrom}
                  onChange={(e) => setEffectiveFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  error={!!effectiveFrom && fromTooSoon}
                  helperText={
                    fromTooSoon && typeof fromDays === 'number'
                      ? `Aviso: por protocolo se solicita con ${MIN_LEAD_DAYS} días de antelación. Has indicado ${fromDays} día(s).`
                      : undefined
                  }
                />

                <TextField
                  fullWidth
                  type="date"
                  label={t('availabilityChange.field.hasta')}
                  value={effectiveTo}
                  onChange={(e) => setEffectiveTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  error={toBeforeFrom}
                  helperText={
                    toBeforeFrom
                      ? t('availabilityChange.error.fechaHastaAnterior')
                      : t('availabilityChange.field.hastaHelper')
                  }
                />
              </Stack>

              {fromTooSoon && (
                <Alert severity="warning">
                  Estás proponiendo un cambio con menos de {MIN_LEAD_DAYS} días de antelación.
                  Recursos Humanos revisará si puede aplicarse antes.
                </Alert>
              )}

              {/* Timezone + motivo (opcional) */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Zona horaria"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  placeholder="Europe/Madrid"
                />
                <FormControl fullWidth>
                  <InputLabel id="reason">{t('availabilityChange.field.motivo')}</InputLabel>
                  <Select
                    labelId="reason"
                    label={t('availabilityChange.field.motivo')}
                    value={reasonCode}
                    onChange={(e) => setReasonCode(e.target.value as string)}
                  >
                    <MenuItem value="">
                      <em>(sin especificar)</em>
                    </MenuItem>
                    <MenuItem value="conciliacion">Conciliación</MenuItem>
                    <MenuItem value="estudios">Estudios</MenuItem>
                    <MenuItem value="salud">Salud</MenuItem>
                    <MenuItem value="otro">Otro</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              {/* ÁMBITO GLOBAL (centros / formatos / horas/semana) */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="centros-label">Centros</InputLabel>
                  <Select
                    multiple
                    labelId="centros-label"
                    label="Centros"
                    value={centrosIds as any}
                    onChange={(e) => setCentrosIds((e.target.value as number[]) ?? [])}
                    input={<OutlinedInput label="Centros" />}
                    renderValue={(selected) => renderSelectedChips(selected as any, centroDict)}
                  >
                    {centros.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel id="formatos-label">
                    {t('availabilityChange.field.formatos')}
                  </InputLabel>
                  <Select
                    multiple
                    labelId="formatos-label"
                    label={t('availabilityChange.field.formatos')}
                    value={formatosIds as any}
                    onChange={(e) => setFormatosIds((e.target.value as number[]) ?? [])}
                    input={<OutlinedInput label={t('availabilityChange.field.formatos')} />}
                    renderValue={(selected) => renderSelectedChips(selected as any, formatoDict)}
                  >
                    {formatos.map((f) => (
                      <MenuItem key={f.id} value={f.id}>
                        {f.nombreFormato}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              {/* Si quieres re-activar horas/semana, descomenta este bloque
              <TextField
                fullWidth
                type="number"
                label="Horas por semana (tope)"
                inputProps={{ min: 1, max: 60 }}
                value={horasSemanales}
                onChange={(e) => setHorasSemanales(e.target.value)}
              />
              */}

              <Divider />

              {/* Semana tipo */}
              <Typography variant="subtitle2">Semana tipo (opcional)</Typography>
              <Stack spacing={1}>
                {Array.from({ length: 7 }, (_, i) => i + 1).map((wd) => (
                  <Box
                    key={wd}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                  >
                    <Box sx={{ width: 96 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {dayLabels[wd]}
                      </Typography>
                    </Box>

                    {/* Hora inicio/fin */}
                    <TextField
                      size="small"
                      label={t('availabilityChange.field.inicio')}
                      placeholder="09:00"
                      value={tmpStart[wd] ?? ''}
                      onChange={(e) => setTmpStart((s) => ({ ...s, [wd]: e.target.value }))}
                      sx={{ width: 120 }}
                    />
                    <TextField
                      size="small"
                      label="Fin"
                      placeholder="14:00"
                      value={tmpEnd[wd] ?? ''}
                      onChange={(e) => setTmpEnd((s) => ({ ...s, [wd]: e.target.value }))}
                      sx={{ width: 120 }}
                    />

                    {/* Centro/formato por rango (opcionales) */}
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <InputLabel id={`centro-${wd}`}>
                        {t('availabilityChange.field.centroOpcional')}
                      </InputLabel>
                      <Select
                        labelId={`centro-${wd}`}
                        label={t('availabilityChange.field.centroOpcional')}
                        value={tmpCentro[wd] ?? ''}
                        onChange={(e) => setTmpCentro((s) => ({ ...s, [wd]: e.target.value }))}
                      >
                        <MenuItem value="">
                          <em>(cualquiera)</em>
                        </MenuItem>
                        <MenuItem value="null">Online / sin centro</MenuItem>
                        {centros.map((c) => (
                          <MenuItem key={c.id} value={String(c.id)}>
                            {c.nombre}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel id={`formato-${wd}`}>Formato (opcional)</InputLabel>
                      <Select
                        labelId={`formato-${wd}`}
                        label="Formato (opcional)"
                        value={tmpFormato[wd] ?? ''}
                        onChange={(e) => setTmpFormato((s) => ({ ...s, [wd]: e.target.value }))}
                      >
                        <MenuItem value="">
                          <em>(cualquiera)</em>
                        </MenuItem>
                        {formatos.map((f) => (
                          <MenuItem key={f.id} value={String(f.id)}>
                            {f.nombreFormato}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <IconButton
                      size="small"
                      onClick={() => addRange(wd)}
                      title={t('availabilityChange.anadirFranja') as string}
                    >
                      <AddIcon />
                    </IconButton>

                    {/* Chips de rangos ya añadidos */}
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                      {(weekly[wd] ?? []).map((r, idx) => (
                        <Box
                          key={`${wd}-${idx}`}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            bgcolor: '#21a598',
                            color: '#fff',
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          {r.start}–{r.end}
                          {r.centroId !== undefined && (
                            <span style={{ marginLeft: 6 }}>
                              ·{' '}
                              {r.centroId === null
                                ? 'Online/sin centro'
                                : (centroDict[r.centroId] ?? `Centro ${r.centroId}`)}
                            </span>
                          )}
                          {r.formatoId !== undefined && (
                            <span style={{ marginLeft: 6 }}>
                              · {formatoDict[r.formatoId] ?? `Formato ${r.formatoId}`}
                            </span>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => removeRange(wd, idx)}
                            sx={{ color: '#fff', p: 0.25 }}
                            title="Eliminar"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>

              <Divider />

              {/* Comentario + Adjuntos */}
              <TextField
                label={t('tickets.teacherAbsence.field.comentario')}
                multiline
                minRows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                fullWidth
              />
              <Button component="label" variant="text">
                {t('tickets.common.adjuntarArchivos')}
                <input hidden multiple type="file" onChange={onChooseFiles} />
              </Button>
              {files.length > 0 && (
                <small>
                  {t('tickets.hrInquiry.archivosSeleccionados', { count: files.length })}
                </small>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={submit}
            variant="contained"
            disabled={loading || perfilLoading || !!perfilError}
          >
            {loading ? <CircularProgress size={20} /> : 'Crear ticket'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.sev} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
